import { useMemo, useState } from "react";
import GroupHeader from "@/components/GroupHeader";
import GroupFooter from "@/components/GroupFooter";
import SEO from "@/components/SEO";
import ProjectCard from "@/components/site/ProjectCard";
import ClosingCTA from "@/components/site/ClosingCTA";
import { PageHero, Reveal } from "@/components/site/Bits";
import { useProjects } from "@/lib/site";

function Filter({ label, options, value, onChange, testid }) {
    return (
        <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
            <span className="mono-label text-muted_text mr-2">{label}</span>
            {["All", ...options].map((o) => (
                <button key={o} type="button" onClick={() => onChange(o)} aria-pressed={value === o}
                        data-testid={`${testid}-${o.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`rounded-full border px-4 py-2 text-sm transition-colors ${value === o ? "bg-forest border-forest text-cream" : "border-hairline hover:border-forest"}`}>
                    {o}
                </button>
            ))}
        </div>
    );
}

export default function Projects() {
    const projects = useProjects();
    const [status, setStatus] = useState("All");
    const [type, setType] = useState("All");
    const statuses = useMemo(() => [...new Set((projects || []).map((p) => p.status))], [projects]);
    const types = useMemo(() => [...new Set((projects || []).map((p) => p.type).filter(Boolean))], [projects]);
    const list = (projects || []).filter((p) => (status === "All" || p.status === status) && (type === "All" || p.type === type));
    const showFilters = (projects || []).length > 3;

    return (
        <div data-testid="projects-page" className="bg-cream text-ink min-h-screen">
            <SEO path="/projects" og="projects" title="Projects — Land development opportunities in Greater Lisbon"
                 description="Verde 2050's portfolio of land development opportunities in Greater Lisbon, Portugal — including an 18-home residential development in Sintra." />
            <GroupHeader />
            <PageHero testid="projects-hero" eyebrow="Projects" image="/v2050-projects-aerial.jpg" imageAlt="Aerial view of undeveloped land with pines and stone walls near Lisbon"
                      title={<>Land we found. <em className="italic text-sage">Places it could become.</em></>}
                      intro="Each project starts with a walk and ends with an approved opportunity ready for the right developer. Show us interest and we'll share the full Information Memorandum." />
            <section className="py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    {showFilters && (
                        <div className="mb-14 space-y-4">
                            <Filter label="Status" options={statuses} value={status} onChange={setStatus} testid="filter-status" />
                            <Filter label="Type" options={types} value={type} onChange={setType} testid="filter-type" />
                        </div>
                    )}
                    {projects === null && <div className="aspect-[16/9] rounded-2xl bg-surface animate-pulse" />}
                    {projects && list.length === 0 && <p className="text-muted_text" data-testid="projects-empty">No projects match this filter yet.</p>}
                    <div className="grid md:grid-cols-2 gap-x-10 gap-y-20" data-testid="projects-grid">
                        {list.map((p, i) => (
                            <Reveal key={p.id} delay={i * 80} className={i === 0 ? "md:col-span-2" : ""}>
                                <ProjectCard p={p} large={i === 0} />
                            </Reveal>
                        ))}
                    </div>
                    {projects && projects.length < 3 && (
                        <Reveal className="mt-24 rounded-2xl border border-dashed border-moss/50 p-10 md:p-14 grid md:grid-cols-12 gap-6 items-center">
                            <p className="md:col-span-8 font-serif text-3xl text-forest leading-tight">More projects are being explored on the ground right now. Not every one is public.</p>
                            <p className="md:col-span-4 text-sm text-muted_text">Developers and investors can ask us about off-market opportunities directly.</p>
                        </Reveal>
                    )}
                </div>
            </section>
            <ClosingCTA testid="projects-contact" eyebrow="Off-market" title={<>Looking for an approved project <em className="italic">to build?</em></>}
                        body="Tell us what you are looking for — location, scale, type — and we'll let you know when something fits." cta="Discuss a project" to="/contact?type=project" />
            <GroupFooter />
        </div>
    );
}
